import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Input, version2 } from 'ui-components/components/input';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { withGuard } from 'ui-components/components/with-guard';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import messages from './messages';
import style from './style.module.less';
import ModalPortal from '../../../../../components/modal-portal';
import { updateSellerFulfillmentProviderSettings, savePackingSlipSharingConfiguration } from '../../api';
import { SharePackingSlip } from '../../share-packing-slip';
import { ProviderPropType } from '../../types';

const VendorSettingsModal = ({ onClose, provider, loadVendors }) => {
  const [isPackingSlipSharing, setIsPackingSlipSharing] = useAsyncSafeState(provider.packingSlipSharing?.isEnabled);

  const [isSaving, setIsSaving] = useAsyncSafeState(false);
  const [didError, setDidError] = useAsyncSafeState(false);
  const [name, setName] = useAsyncSafeState(provider?.partnerName ?? '');

  const saveSettings = useCallback(
    async (settingsToSave) => {
      setIsSaving(true);
      setDidError(false);
      try {
        await updateSellerFulfillmentProviderSettings(provider.sellerFillProviderId, settingsToSave);

        const savePackingSlipSharingRequest = {
          clientExternalConnectionIds: [provider.externalClientConnectionId],
          isPackingSlipSharing,
        };

        await savePackingSlipSharingConfiguration(savePackingSlipSharingRequest);

        setIsSaving(false);
        onClose();
        loadVendors();
      } catch {
        setIsSaving(false);
        setDidError(true);
      }
    },
    [
      setIsSaving,
      setDidError,
      provider.sellerFillProviderId,
      provider.externalClientConnectionId,
      onClose,
      loadVendors,
      isPackingSlipSharing,
    ],
  );

  const onSaveSettings = useCallback(() => {
    const settingsToSave = { name: name.trim() };
    saveSettings(settingsToSave);
  }, [name, saveSettings]);

  const onChange = useCallback(
    (e) => {
      const updatedName = e.target.value;
      setName(updatedName);
    },
    [setName],
  );

  const onCloseErrorBanner = useCallback(async () => setDidError(false), [setDidError]);

  const packingSlipSharingChanged = useCallback(() => {
    setIsPackingSlipSharing((prev) => !prev);
  }, [setIsPackingSlipSharing]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal size='small'>
        <ModalHeader>
          <FormattedMessage {...messages.modalHeader} />
        </ModalHeader>
        {didError ? (
          <BannerMessageV2
            content={<FormattedMessage {...messages.errorBannerText} />}
            onClose={onCloseErrorBanner}
            status={BannerStatus.Error}
          />
        ) : null}
        <ModalBody className={style['ss-settings-modal-body']}>
          {isSaving ? (
            <LoadingIndicator className={style['loading-indicator']} />
          ) : (
            <>
              <div className={style['name-input']}>
                <Input
                  label={<FormattedMessage {...messages.nameInputLabel} />}
                  onChange={onChange}
                  value={name ?? ''}
                  version={version2}
                />
              </div>
              <SharePackingSlip isPackingSlipSharing={isPackingSlipSharing} packingSlipSharingChanged={packingSlipSharingChanged} />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <ModalFooterCancelButton disabled={isSaving} />
          <Button disabled={isSaving ?? !name.length} isLoading={isSaving} onClick={onSaveSettings} theme='primary'>
            <FormattedMessage {...messages.saveButton} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

VendorSettingsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  loadVendors: PropTypes.func.isRequired,
  provider: ProviderPropType.isRequired,
};

const ModalWithGuard = withGuard(VendorSettingsModal);
export { ModalWithGuard as VendorSettingsModal };
